import PropTypes from 'prop-types';
import React from 'react';
import useTranslatedText from '../../../hooks/useTranslatedText';
import MenuGroup from './MenuGroup';
import MenuList from './MenuList';
import MenuSelector from './MenuSelector';
import styles from './menu.module.scss';

/**
 * A regular L1+ menu that displays categories and espots
 *
 * @param {Object} props - The props for the Menu component.
 * @param {string} [props.theme="secondary"] - Primary (L0), secondary (L1+),
 * or tertiary (footer).
 * @param {string} props.heading - The name of the category.
 * @param {Object} [props.allLink] - The link to the category we're currently viewing.
 * @param {Array<Object>} props.regularLinks - Array of regular category links.
 * @param {string} props.regularLinks[].label - The name of the category.
 * @param {string} [props.regularLinks[].link] - The URL, if the link should not open
 * another menu level.
 * @param {boolean} [props.regularLinks[].hasSpecialNavInteraction] - Whether the link title
 * is an espot.
 * @param {string} [props.regularLinks[].espotIdentifier] - Identifier for the espot
 * in place of the link title.
 * @param {Array<Object>} props.featuredLinks - Array of collection links.
 * @param {Array<Object>} props.clearanceLinks - Array of clearance category links.
 * @param {Function} props.handleClick - Callback function triggered when a link is clicked.
 * @returns {JSX.Element} The rendered Menu component.
 */
const Menu = ({
  // primary is the L0 menu and tertiary is footer links, so most links are secondary
  theme = 'secondary',
  heading,
  allLink,
  regularLinks,
  featuredLinks,
  clearanceLinks,
  espotIdentifier,
  handleClick,
}) => {
  const collectionsHeading = useTranslatedText(
    'navFeaturedGroupHeader',
    { fallback: 'Collections' },
  );
  const allLabel = useTranslatedText(
    'allCategoryName',
    { fallback: 'Shop All', replacements: [heading] },
  );
  const categoriesLabel = useTranslatedText(
    'categoryNameSubcats',
    { fallback: 'Regular categories', replacements: [heading] },
  );
  const featuredCategoriesLabel = useTranslatedText(
    'categoryNameFeatured',
    { fallback: 'Featured categories', replacements: [heading] },
  );
  return (
    <>
      <MenuGroup
        heading={heading}
        label={categoriesLabel.value}
        // Add an "All (category name)" link at the top if we have it
        preEspotContent={
          allLink ? (
            <ul className={styles.list}>
              <li>
                <MenuSelector
                  id="all"
                  label={allLabel.value}
                  link={allLink}
                  onClick={handleClick}
                  theme={theme}
                >
                  {allLabel.value}
                </MenuSelector>
              </li>
            </ul>
          ) : null
        }
        topEspotIdentifier={espotIdentifier ? `${espotIdentifier}-nav-mobile` : undefined}
      >
        {/* Regular categories */}
        <MenuList
          bottomEspotIdentifier={espotIdentifier ? `${espotIdentifier}-cat-column-regular-bottom-mobile` : undefined}
          handleClick={handleClick}
          links={regularLinks}
          theme={theme}
          // Logic below is temp workaround for duplicate "all" link in top espot
          // Once link is removed from espot, can be a check for espotIdentifier only
          topEspotIdentifier={(!allLink && espotIdentifier) ? `${espotIdentifier}-cat-column-regular-top-mobile` : undefined}
        />
        {/* Clearance categories */}
        <MenuList
          bottomEspotIdentifier={espotIdentifier ? `${espotIdentifier}-cat-column-clearance-bottom-mobile` : undefined}
          handleClick={handleClick}
          // If all links are clearance, we style them as regular links
          isClearance={!((regularLinks.length === 0 && featuredLinks.length === 0))}
          links={clearanceLinks}
          theme={theme}
          topEspotIdentifier={espotIdentifier ? `${espotIdentifier}-cat-column-clearance-top-mobile` : undefined}
        />
      </MenuGroup>
      {featuredLinks.length > 0 ? (
        <MenuGroup
          heading={collectionsHeading.value}
          headingKey={collectionsHeading.key}
          label={featuredCategoriesLabel.value}
        >
          {/* Featured categories */}
          <MenuList
            bottomEspotIdentifier={espotIdentifier ? `${espotIdentifier}-cat-column-featured-bottom-mobile` : undefined}
            handleClick={handleClick}
            links={featuredLinks}
            theme={theme}
            topEspotIdentifier={espotIdentifier ? `${espotIdentifier}-cat-column-featured-top-mobile` : undefined}
          />
        </MenuGroup>
      ) : null}
    </>
  );
};

Menu.propTypes = {
  allLink: PropTypes.string,
  heading: PropTypes.string.isRequired,
  regularLinks: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      link: PropTypes.string,
      hasSpecialNavInteraction: PropTypes.bool,
      espotIdentifier: PropTypes.string,
    }),
  ).isRequired,
  featuredLinks: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      link: PropTypes.string,
      hasSpecialNavInteraction: PropTypes.bool,
      espotIdentifier: PropTypes.string,
    }),
  ).isRequired,
  clearanceLinks: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      link: PropTypes.string,
      hasSpecialNavInteraction: PropTypes.bool,
      espotIdentifier: PropTypes.string,
    }),
  ).isRequired,
  espotIdentifier: PropTypes.string,
  handleClick: PropTypes.func.isRequired,
  theme: PropTypes.string,
};

export default Menu;
